import { useCallback, useState } from 'react';

import { ensureDefined } from '@e-socna/utils';
import {
  PRINTER_BODY_ID,
  PRINTER_FULLY_COMPILED_CLASSNAME,
} from '@e-socna/printer-contract';
import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { Id, PrintStyleId } from '@e-socna/data-layer';

import { PrinterForm } from '../../../../forms/Printer';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { PrinterFormValues } from '../../../../forms/Printer';

import { EditorPageStyles } from '../../../EditorPageStyles';
import { Loading } from '../../../Loading';
import { PRINTER_CLASS_SUFFIX } from '../../../GlobalStyles/utils/get-socna-printer-styles.util';

import { useDocumentEditorContext } from '../../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { useDocumentResolverContext } from '../../contexts/DocumentResolverContext/hooks/useDocumentResolverContext.hook';
import { useContentGenerator } from '../../hooks/useContentGenerator.hook';
import type { Editable } from '../../types/editable.type';

import { Section } from './Section';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const PrinterTabPanel = <E extends Editable>() => {
  const {
    enrichedVirtualRoot,
    props: {
      getters: { editable: getEditable, printing: getPrinting },
    },
    state,
  } = useDocumentEditorContext<E>();

  const { printStylesIdMap } = useReferencesContext();
  const { contentToPrint, printerSettings, setPrinterSettings } =
    useDocumentResolverContext();

  const useSubmitFactory = useCallback(
    (callback?: () => void) =>
      (
        values: Readonly<PrinterFormValues>,
        {
          setSubmitting,
        }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
      ) => {
        setPrinterSettings(values);
        callback?.();
        setSubmitting(false);
      },
    [setPrinterSettings],
  );

  const [loadedSections, setLoadedSections] =
    useState<readonly Id[]>(EMPTY_ARRAY);

  const handleReset = useCallback(() => {
    setPrinterSettings(undefined);
    setLoadedSections(EMPTY_ARRAY);
  }, [setPrinterSettings]);

  const {
    selectedGroup: { paperFormat, orientation, sections },
  } = ensureDefined({
    selectedGroup:
      contentToPrint[
        printerSettings?.currentGroupIndex
          ? parseInt(printerSettings.currentGroupIndex, 10)
          : 0
      ],
  });

  const { code, title } = getEditable(state.editable);

  const {
    printSettings: { printStyleId },
  } = getPrinting(state.editable);
  const { printStyle } = ensureDefined({
    printStyle: printStylesIdMap[printStyleId as PrintStyleId],
  });

  const { data: titleGenerationData, isSuccess: titleGenerationIsSuccess } =
    useContentGenerator({
      template: title,
    });

  const { data: codeGenerationData, isSuccess: codeGenerationIsSuccess } =
    useContentGenerator({
      template: code,
    });

  const onGeneratedHandlerFactory = useCallback(
    (sectionId: Id) => () => {
      setLoadedSections((prev) => [...new Set([...prev, sectionId])]);
    },
    [setLoadedSections],
  );

  const canDisplaySections = !!printerSettings;
  const isWholeContentCompiled =
    sections.every((section) => loadedSections.includes(section.id)) &&
    (!title || titleGenerationIsSuccess) &&
    (!code || codeGenerationIsSuccess);

  if (enrichedVirtualRoot.isFullyLoaded) {
    return (
      <>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!(canDisplaySections && !isWholeContentCompiled) && (
          <Loading showNotification={false} />
        )}
        <PrinterForm
          contentToPrint={contentToPrint}
          onReset={handleReset}
          useSubmitFactory={useSubmitFactory}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(title && titleGenerationData?.generated
            ? { title: titleGenerationData.generated }
            : EMPTY_OBJECT)}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(code && codeGenerationData?.generated
            ? { code: codeGenerationData.generated }
            : EMPTY_OBJECT)}
        />
        <EditorPageStyles />
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!canDisplaySections && (
          <div
            className={`${printStyle.code}${PRINTER_CLASS_SUFFIX}${isWholeContentCompiled ? ` ${PRINTER_FULLY_COMPILED_CLASSNAME}` : EMPTY_STRING}`}
            id={PRINTER_BODY_ID}
          >
            {sections.map((section, index, sections) => (
              <Section
                isLast={index === sections.length - 1}
                key={section.id}
                onGenerated={onGeneratedHandlerFactory(section.id)}
                orientation={orientation}
                paperFormat={paperFormat}
                section={section}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  return <Loading showNotification={false} />;
};
